import type { SettlementLineView } from '@cuito/types';
import { AMOUNT_UNAVAILABLE_TEXT, formatServerYuan } from '../../lib/schemeForm';

/**
 * 结算明细表（UI 规范 554 行：订单号 / 商品 / 数量 / 单价 / 毛额 / 已退 / 佣金）。
 *
 * `refundedQty` / `refundedAmount` / `refunded` 都是**服务端字段**：行是否退过由服务端判，
 * 前端不重算（`refundedQty > 0` 这种判据在「退款发生在结算后」时会给错答案 —— 那时退款在**反向单**上）。
 */
interface Props {
  lines: SettlementLineView[];
  loading: boolean;
  error: string | null;
}

function amountText(amount: string | null | undefined): string {
  if (amount === null || amount === undefined || amount === '') return AMOUNT_UNAVAILABLE_TEXT;
  return formatServerYuan(amount) ?? AMOUNT_UNAVAILABLE_TEXT;
}

export default function SettlementLinesTable({ lines, loading, error }: Props) {
  if (loading) return <p className="py-4 text-center text-gray-500">加载中…</p>;
  if (error !== null) return <p className="py-4 font-bold text-red-600">{error}</p>;
  if (lines.length === 0) return <p className="py-4 text-gray-500">暂无待结算明细</p>;

  return (
    <table className="w-full text-sm" data-testid="settlement-lines">
      <thead>
        <tr className="border-b-2 border-black text-left">
          <th className="py-2">订单号</th>
          <th className="py-2">商品</th>
          <th className="py-2">数量</th>
          <th className="py-2">单价</th>
          <th className="py-2">毛额</th>
          <th className="py-2">已退</th>
          <th className="py-2">佣金</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((line) => (
          <tr
            key={line.id}
            className="border-b border-gray-200"
            data-testid="settlement-line-row"
            data-order-line-id={line.orderLineId}
            data-refunded={line.refunded ? 'true' : 'false'}
          >
            <td className="py-2">{line.orderNo}</td>
            <td className="py-2">{line.productName}</td>
            <td className="py-2">{line.quantity}</td>
            <td className="py-2">{amountText(line.unitPrice)}</td>
            <td className="py-2" data-testid="settlement-line-amount">
              {amountText(line.amount)}
            </td>
            <td className="py-2">
              {line.refundedQty} 件 / {amountText(line.refundedAmount)}
            </td>
            <td className="py-2" data-testid="settlement-line-commission">
              {amountText(line.commission)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
